import React from "react";
import { CheckCircle, Brain, Zap } from "lucide-react";
import bannerLogo2 from "../../../Assets/Banner-Image-2.png";
import { motion } from "framer-motion";

const HiringNewCandidate = ({ handleOpenModal }) => {
  const waitlistUrl = "https://portal.selfsync.ai/sign-up";

  const keyFeatures = [
    {
      icon: <Brain className="w-8 h-8 text-purple-600" />,
      title: "AI Behavioral Analysis",
      description: "Our AI reads personality patterns like a psychologist - instantly revealing who will thrive in your specific roles."
    },
    {
      icon: <Zap className="w-8 h-8 text-cyan-500" />,
      title: "Instant Candidate Insights",
      description: "Get comprehensive personality reports in seconds, not weeks. Know exactly who to interview before you waste time."
    },
    {
      icon: <CheckCircle className="w-8 h-8 text-green-500" />,
      title: "Bias-Free Hiring Decisions",
      description: "Remove unconscious bias from your process. Our AI evaluates pure potential, not demographics or interview performance."
    }
  ];

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="py-12 px-6 md:px-14 lg:px-20 bg-white"
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.2 } },
      }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-16">
          
          {/* Left Content */}
          <motion.div
            className="lg:w-1/2 text-center lg:text-left"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.h2
              className="text-4xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              Screen{" "}
              <span className="bg-gradient-to-r from-purple-600 to-cyan-500 text-transparent bg-clip-text">
                New Candidates
              </span>{" "}
              in Minutes
            </motion.h2>

            <motion.p
              className="text-xl text-gray-600 mb-10 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              Stop wasting time on bad interviews. Our AI reveals candidate potential before you ever meet them.
            </motion.p>

            <div className="space-y-6 mb-10">
              {keyFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-4 text-left"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.button
              className="custom-button text-white font-bold px-6 py-2 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg text-base md:text-lg transform hover:scale-105"
              onClick={() => window.open(waitlistUrl, "_blank")}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
            >
              Try Free Assessment
            </motion.button>
            <p className="text-sm text-start text-gray-500 mt-3 max-md:text-center">No credit card required </p>
          </motion.div>

          {/* Right Image */}
          <motion.div
            className="lg:w-1/2 relative"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src={bannerLogo2}
                alt="Diverse group of professional candidates"
                className="object-cover w-full rounded-2xl"
              />
              
              {/* Floating Success Rate Card */}
              <div className="absolute  bottom-2  left-1 md:left-0 bg-white/95 backdrop-blur-sm rounded-lg p-2 md:p-4 shadow-lg">
                <div className="text-center">
                  <div className="md:text-2xl font-bold text-green-500">99%</div>
                  <div className="text-xs text-gray-600">Success Rate</div>
                </div>
              </div>
            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-purple-300 to-cyan-300 opacity-30 rounded-full blur-3xl -z-10"></div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default HiringNewCandidate;
